import React, { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

type Enrollment = 'OPT_IN' | 'OPT_OUT';
type Features = Record<string, Enrollment>;

const DEBOUNCE_MS = 600;

/**
 * The creative-enhancement switches.
 *
 * Split out and given its own local state because the previous version saved by
 * PUT-then-refetch: every click reset the page and disabled every switch until
 * the round trip finished, so flipping several in a row was impossible.
 *
 * Now a click updates local state immediately and the write is debounced. The
 * payload is built from a REF of current state rather than from props, so ten
 * clicks in a second produce one PUT carrying all ten — building it from a
 * captured prop would send the state as of whichever click won the race.
 */
export default function EnhancementToggles({
  keys,
  value,
  observedLiveOptIn,
  onSave,
}: {
  keys: string[];
  value: Features;
  observedLiveOptIn: string[];
  onSave: (features: Features) => Promise<void>;
}) {
  const [features, setFeatures] = useState<Features>(value);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [error, setError] = useState('');

  const featuresRef = useRef(features);
  featuresRef.current = features;
  // The last state the server is known to hold, to roll back to on failure.
  const committedRef = useRef(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Adopt a genuinely new server value (first load, or a reload elsewhere on
  // the page) without clobbering edits the operator is mid-way through making.
  useEffect(() => {
    if (status === 'idle') {
      setFeatures(value);
      committedRef.current = value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const flush = useCallback(async () => {
    const pending = featuresRef.current;
    setStatus('saving');
    setError('');
    try {
      await onSave(pending);
      committedRef.current = pending;
      setStatus('saved');
      setTimeout(() => setStatus(s => (s === 'saved' ? 'idle' : s)), 1500);
    } catch (err) {
      // Put the switches back where the server actually is, so the UI never
      // shows an enhancement as off when it is still on in the ad account.
      setFeatures(committedRef.current);
      setStatus('error');
      const anyErr = err as { response?: { data?: { error?: string } }; message?: string };
      setError(anyErr?.response?.data?.error || anyErr?.message || 'Could not save.');
    }
  }, [onSave]);

  const toggle = (key: string, on: boolean) => {
    setFeatures(prev => ({ ...prev, [key]: on ? 'OPT_IN' : 'OPT_OUT' }));
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, DEBOUNCE_MS);
  };

  const optedIn = keys.filter(k => features[k] === 'OPT_IN');

  return (
    <>
      <Stack direction="row" alignItems="center" sx={{ mb: 1, gap: 1 }}>
        <Typography variant="subtitle1" sx={{ flex: 1 }}>Creative enhancements</Typography>
        {status === 'saving' && <Typography variant="caption" color="text.secondary">Saving…</Typography>}
        {status === 'saved' && <Typography variant="caption" color="success.main">Saved</Typography>}
      </Stack>

      {status === 'error' && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {optedIn.length === 0 ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          All {keys.length} are sent as OPT_OUT on every ad.
        </Alert>
      ) : (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>{optedIn.length} left on</AlertTitle>
          {optedIn.join(', ')}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        The six marked &ldquo;on in live ads&rdquo; are on in your existing ads only because nothing turned
        them off — a launched batch was read back with all 83 of Meta&rsquo;s stored feature keys OPT_OUT.
      </Alert>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.5 }}>
        {keys.map(key => (
          <Stack key={key} direction="row" alignItems="center" spacing={1}>
            {/* Never disabled while saving — that was the thing that made rapid
                toggling impossible. */}
            <Switch
              size="small"
              checked={features[key] === 'OPT_IN'}
              onChange={e => toggle(key, e.target.checked)}
            />
            <Typography variant="body2" sx={{ flex: 1 }}>{key}</Typography>
            {observedLiveOptIn.includes(key) && <Chip size="small" variant="outlined" label="on in live ads" />}
          </Stack>
        ))}
      </Box>
    </>
  );
}
